import { authService } from '@thistle/mainframe-auth';
import { AppState, Cycle, Facility, APIRoutePrefixWithStatus, PackLine, Delivery, Alert } from '../types/index.js';

// Configuration for which state keys should be persisted
const PERSISTENT_KEYS = {
  selectedFacility: { key: 'selectedFacility', default: null },
  linkedPackLine: { key: 'linkedPackLine', default: null },
  facilityId: { key: 'facilityId', default: null }
} as const;

type PersistentStateKeys = (typeof PERSISTENT_KEYS)[keyof typeof PERSISTENT_KEYS]['key'];

// Helper function to save persistent app state to localStorage
function savePersistentState(state: AppState) {
  try {
    const persistentData: Record<string, any> = {};
    Object.values(PERSISTENT_KEYS).forEach(({ key }) => {
      persistentData[key] = state[key as keyof AppState];
    });
    localStorage.setItem('bob_app_state', JSON.stringify(persistentData));
  } catch (error) {
    console.error('Error saving persistent state:', error);
  }
}

type Listener<T> = (state: T) => void;

class Store<T> {
  private state: T;
  private listeners: Set<Listener<T>> = new Set();

  constructor(initialState: T) {
    this.state = initialState;
  }

  getState(): T {
    return this.state;
  }

  setState(updater: (state: T) => T): void {
    this.state = updater(this.state);
    this.notifyListeners();
    savePersistentState(this.state as AppState);
  }

  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
  }
}

// Load persistent app state from localStorage on startup
function loadStoredAppState(): Pick<AppState, PersistentStateKeys> {
  try {
    const storedData = localStorage.getItem('bob_app_state');
    const parsed = storedData ? JSON.parse(storedData) : {};
    const result = {} as Pick<AppState, PersistentStateKeys>;

    Object.values(PERSISTENT_KEYS).forEach(({ key, default: defaultValue }) => {
      result[key as keyof typeof result] = parsed[key] ?? defaultValue;
    });

    return result;
  } catch (error) {
    console.error('Error loading stored app state:', error);
    try {
      localStorage.removeItem('bob_app_state');
    } catch (e) {}

    // Return defaults from PERSISTENT_KEYS config
    const result = {} as Pick<AppState, PersistentStateKeys>;
    Object.values(PERSISTENT_KEYS).forEach(({ key, default: defaultValue }) => {
      (result as any)[key] = defaultValue;
    });
    return result;
  }
}

const persistedState = loadStoredAppState();

const initialState: AppState = {
  // Auth state
  isAuthenticated: authService.isAuthenticated(),

  // Data state
  facilities: [],
  selectedFacility: persistedState.selectedFacility,
  currentCycle: null,
  routePrefixes: [],
  packLines: [],
  linkedPackLine: persistedState.linkedPackLine,
  deliveries: [],
  expandedRoutePrefix: null,
  expandedRoute: null,
  expandedRoutesData: [],
  expandedDeliveriesData: [],

  // UI state
  isPackLinesExpanded: false,
  isDrawerOpen: false,
  isMenuOpen: false,
  isLoading: false,
  error: null,
  alerts: [],
  facilityId: persistedState.facilityId
};

export const appStore = new Store(initialState);

// Action creators
export const actions = {
  // Data actions
  setFacilities: (facilities: Facility[]) => {
    appStore.setState(state => ({ ...state, facilities }));
  },

  setSelectedFacility: (facility: Facility | null) => {
    appStore.setState(state => ({ ...state, selectedFacility: facility, facilityId: facility?.id || 1 }));
  },

  setCurrentCycle: (cycle: Cycle | null) => {
    appStore.setState(state => ({ ...state, currentCycle: cycle }));
  },

  setRoutePrefixes: (routePrefixes: APIRoutePrefixWithStatus[]) => {
    appStore.setState(state => ({ ...state, routePrefixes }));
  },

  setPackLines: (packLines: PackLine[]) => {
    appStore.setState(state => ({ ...state, packLines }));
  },

  setLinkedPackLine: (packLine: PackLine | null) => {
    appStore.setState(state => ({ ...state, linkedPackLine: packLine }));
    if (packLine) {
      actions.updatePackLine(packLine)
    }
  },

  setDeliveries: (deliveries: Delivery[]) => {
    appStore.setState(state => ({ ...state, deliveries: deliveries }));
  },

  togglePackLinesExpanded: () => {
    appStore.setState(state => ({ ...state, isPackLinesExpanded: !state.isPackLinesExpanded }));
  },

  toggleDrawer: () => {
    appStore.setState(state => ({ ...state, isDrawerOpen: !state.isDrawerOpen }));
    actions.clearAlerts("drawer");
    // Cancel any pending auto-close when user manually toggles
    import('../services/barcodeScanner.js').then(({ barcodeScanner }) => {
      barcodeScanner.cancelAutoClose();
    });
  },

  closeDrawer: () => {
    appStore.setState(state => ({ ...state, isDrawerOpen: false }));
    actions.clearAlerts("drawer");
  },

  toggleMenu: () => {
    appStore.setState(state => ({ ...state, isMenuOpen: !state.isMenuOpen }));
  },

  closeMenu: () => {
    appStore.setState(state => ({ ...state, isMenuOpen: false }));
  },

  setLoading: (isLoading: boolean) => {
    appStore.setState(state => ({ ...state, isLoading }));
  },

  setError: (error: string | null) => {
    appStore.setState(state => ({ ...state, error }));
  },

  addPackLine: (packLine: PackLine) => {
    appStore.setState(state => ({
      ...state,
      packLines: [...state.packLines, packLine]
    }));
  },

  updatePackLine: (updatedPackLine: PackLine) => {
    appStore.setState(state => ({
      ...state,
      packLines: state.packLines.map(pl =>
        pl.id === updatedPackLine.id ? updatedPackLine : pl
      )
    }));
  },

  removePackLine: (packLineId: number) => {
    appStore.setState(state => ({
      ...state,
      packLines: state.packLines.filter(pl => pl.id !== packLineId),
      linkedPackLine: state.linkedPackLine?.id === packLineId ? null : state.linkedPackLine
    }));
  },

  setFacilityId: (facilityId: number) => {
    appStore.setState(state => ({ ...state, facilityId }));
  },

  addAlert: (message: string, type: 'success' | 'error' | 'warning' = 'success', scope: 'global' | 'drawer' = 'global', dedupe = false) => {
    if (dedupe && appStore.getState().alerts.some(alert => alert.message === message)) {
      return;
    }
    const alert: Alert = {
      id: `${Date.now()}-${crypto.randomUUID().slice(0,7)}`,
      message,
      type,
      scope,
      timestamp: Date.now()
    };
    appStore.setState(state => ({ ...state, alerts: [...state.alerts, alert] }));
  },

  removeAlert: (alertId: string) => {
    appStore.setState(state => ({ ...state, alerts: state.alerts.filter(alert => alert.id !== alertId) }));
  },

  clearAlerts: (scope?: 'global' | 'drawer') => {
    appStore.setState(state => ({
      ...state,
      alerts: scope ? state.alerts.filter(alert => alert.scope !== scope) : []
    }));
  },

  setExpandedRoutePrefix: (routePrefixId: number | null, cycleId: number | null) => {
    appStore.setState(state => ({
      ...state,
      expandedRoutePrefix: routePrefixId && cycleId ? { routePrefixId, cycleId } : null,
      expandedRoutesData: routePrefixId ? state.expandedRoutesData : []
    }));
  },

  setExpandedRoute: (routeId: number | null) => {
    appStore.setState(state => ({
      ...state,
      expandedRoute: routeId ? { routeId } : null,
      expandedDeliveriesData: routeId ? state.expandedDeliveriesData : []
    }));
  },

  setExpandedRoutesData: (routes: any[]) => {
    appStore.setState(state => ({
      ...state,
      expandedRoutesData: routes
    }));
  },

  setExpandedDeliveriesData: (deliveries: any[]) => {
    appStore.setState(state => ({
      ...state,
      expandedDeliveriesData: deliveries
    }));
  }
};
